import { useMemo } from 'react';

import { Table } from '@tanstack/react-table';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { useDataTable } from '@/hooks/use-data-table';
import { ApiError } from '@/modules/common/api/errors';

import { getTimeOffsTableColumns } from '../components/table/time-offs-table-columns';
import { MEMBER_TIME_OFFS_TABLE_ID } from '../constants/member-time-offs-query-keys';
import { TimeOffRequest } from '../types/time-off-request';
import { useMemberTimeOffRequests } from './use-member-time-off-requests';

interface UseMemberTimeOffsTableResult {
  table: Table<TimeOffRequest>;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** Total count from the latest page (drives the "Requests: N" label). */
  total: number;
  /** Resolved error message (i18n) or undefined. */
  errorMessage?: string;
  saveColumnSizing: () => void;
}

/**
 * Member-scoped twin of `useTimeOffsTable`: wires the member time-off requests
 * query to a TanStack table instance using a distinct table id so column
 * sizing/visibility don't collide with the personal time-offs page.
 */
export const useMemberTimeOffsTable = (
  memberId: string | undefined,
  filters: TableFilterState,
  resolveErrorCode: (code: string) => string,
  onEditRequest: (request: TimeOffRequest) => void
): UseMemberTimeOffsTableResult => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useMemberTimeOffRequests(memberId, filters);

  const rows = useMemo<TimeOffRequest[]>(() => data?.items ?? [], [data]);
  const columns = useMemo(() => getTimeOffsTableColumns({ onEditRequest }), [onEditRequest]);

  const { table, saveColumnSizing } = useDataTable<TimeOffRequest>({
    data: rows,
    columns,
    getRowId: row => String(row.id),
    tableId: MEMBER_TIME_OFFS_TABLE_ID,
  });

  const errorMessage = error
    ? error instanceof ApiError
      ? resolveErrorCode(error.code)
      : error.message
    : undefined;

  return {
    table,
    isLoading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    total: data?.pagination.total ?? 0,
    errorMessage,
    saveColumnSizing,
  };
};
